import { c as _c } from "react/compiler-runtime";
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { CYLINDO_OPTIONS, CYLINDO_ACCOUNT_ID, FABRIC_PRODUCT_OPTION_ID, LEATHER_PRODUCT_OPTION_ID, PANTONE_FABRIC_CONFIG } from 'commons/constants';
import { orientationOptionIds } from 'components/consumer/OrientationPDPModal';
import { logError } from 'commons/logger';
import usePDPOptions from 'components/consumer/PDP/state/context/usePDPOptions';
import usePDPBasicDetails from 'components/consumer/PDP/state/context/usePDPBasicDetails';
import useCylindoDimensionShots from '../useCylindoDimensionShots';
import { probeLatestVersion } from '../useCylindoDimensionShots/probeLatestVersion';
const DISABLED_UPHOLSTERY = [];
const sunbrellaNameMismatchOptions = {
  'Sunbrella Premier Fog': 'Premier Fog',
  'Sunbrella Premier Indigo': 'Premier Indigo',
  'Sunbrella Premier Wisteria': 'Premier Wisteria',
  'Sunbrella Premier Lagoon': 'Premier Lagoon',
  'Sunbrella Premier Sea': 'Premier Sea'
};
const CYLINDO_FRAMES_TO_SHOW = [1, 5, 9, 13, 17];
const CYLINDO_CONTENT_API = 'https://content.cylindo.com/api/v2';
const cylindoConfigPromises = {};
const fetchCylindoConfig = async productId => {
  if (!cylindoConfigPromises[productId]) {
    cylindoConfigPromises[productId] = axios.get(`${CYLINDO_CONTENT_API}/${CYLINDO_ACCOUNT_ID}/products/${productId}/configuration`);
  }
  return cylindoConfigPromises[productId];
};
const usePDPCylindoViewer = t0 => {
  const $ = _c(50);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    isPDPDesktop: t2,
    material: t3
  } = t1;
  const isPDPDesktop = t2 === undefined ? false : t2;
  const material = t3 === undefined ? null : t3;
  const [loading, setLoading] = useState(false);
  const [cylindoProductConfig, setCylindoProductConfig] = useState(null);
  const [cylindoVersion, setCylindoVersion] = useState(null);
  const {
    data: basicDetails
  } = usePDPBasicDetails();
  const {
    data: t4
  } = usePDPOptions();
  const {
    selectedOptionsExtended
  } = t4;
  const productId = basicDetails?.id;
  const isCylindoProduct = !!basicDetails?.custom_properties?.cylindo_product;
  let t5;
  let t6;
  if ($[2] !== isCylindoProduct || $[3] !== productId) {
    t5 = () => {
      if (productId && isCylindoProduct) {
        setCylindoProductConfig(null);
        setCylindoVersion(null);
        setLoading(true);
        fetchCylindoConfig(productId).then(res => {
          if (!res.data) {
            return null;
          }
          setCylindoProductConfig(res.data);
          return probeLatestVersion(productId, res.data.version).then(setCylindoVersion);
        }).catch(_temp).finally(() => {
          setLoading(false);
        });
      }
      return () => {
        setCylindoProductConfig(null);
        setCylindoVersion(null);
      };
    };
    t6 = [productId, isCylindoProduct];
    $[2] = isCylindoProduct;
    $[3] = productId;
    $[4] = t5;
    $[5] = t6;
  } else {
    t5 = $[4];
    t6 = $[5];
  }
  useEffect(t5, t6);
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      ...sunbrellaNameMismatchOptions,
      ...PANTONE_FABRIC_CONFIG.pantoneFabricCylindoNameMapping
    };
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  const nameMismatchOptions = t7;
  let t8;
  if ($[7] !== cylindoProductConfig || $[8] !== material?.label || $[9] !== productId || $[10] !== selectedOptionsExtended) {
    t8 = () => {
      if (!productId || !cylindoProductConfig || !selectedOptionsExtended) {
        return null;
      }
      const upholsteryName = material?.label ?? selectedOptionsExtended[FABRIC_PRODUCT_OPTION_ID]?.value?.value ?? selectedOptionsExtended[LEATHER_PRODUCT_OPTION_ID]?.value?.value ?? null;
      if (!upholsteryName) {
        return null;
      }
      const correctedUpholsteryName = nameMismatchOptions[upholsteryName] || upholsteryName;
      return correctedUpholsteryName.trim().replace("\u2122", "").toUpperCase();
    };
    $[7] = cylindoProductConfig;
    $[8] = material?.label;
    $[9] = productId;
    $[10] = selectedOptionsExtended;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  material?.label;
  let t9;
  if ($[12] !== t8) {
    t9 = t8();
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  const upholsteryKey = t9;
  const hasUpholsteryOption = !!upholsteryKey;
  let t10;
  if ($[14] !== cylindoVersion || $[15] !== upholsteryKey) {
    t10 = {
      upholsteryKey,
      cylindoVersion
    };
    $[14] = cylindoVersion;
    $[15] = upholsteryKey;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const dimensionShotsData = useCylindoDimensionShots(t10);
  let t11;
  bb0: {
    if (!upholsteryKey || DISABLED_UPHOLSTERY.includes(upholsteryKey)) {
      t11 = undefined;
      break bb0;
    }
    let t12;
    if ($[17] !== cylindoProductConfig.features || $[18] !== upholsteryKey) {
      const upholsteryFeature = cylindoProductConfig.features?.find(_temp2);
      t12 = upholsteryFeature?.options.find(opt => opt.code?.toUpperCase() === upholsteryKey);
      $[17] = cylindoProductConfig.features;
      $[18] = upholsteryKey;
      $[19] = t12;
    } else {
      t12 = $[19];
    }
    const upholsteryOption = t12;
    if (!upholsteryOption) {
      t11 = undefined;
      break bb0;
    }
    let t13;
    if ($[20] !== upholsteryOption.code) {
      t13 = {
        [CYLINDO_OPTIONS.UPHOLSTERY]: upholsteryOption.code
      };
      $[20] = upholsteryOption.code;
      $[21] = t13;
    } else {
      t13 = $[21];
    }
    t11 = t13;
  }
  const upholsteryFeatureDetails = t11;
  const isUpholsteryFeatureAvailable = !!upholsteryFeatureDetails;
  const orientationId = orientationOptionIds.find(id => selectedOptionsExtended[id]);
  const orientationValue = selectedOptionsExtended[orientationId]?.value?.value;
  let t12;
  if ($[22] !== orientationValue) {
    t12 = orientationValue?.toUpperCase();
    $[22] = orientationValue;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  const orientationKey = t12;
  const hasOrientationOption = !!orientationKey;
  let t13;
  if ($[24] !== cylindoProductConfig?.features || $[25] !== orientationKey) {
    t13 = () => {
      if (!orientationKey) {
        return;
      }
      const orientationFeature = cylindoProductConfig?.features?.find(_temp3);
      const orientationKeyStr = String(orientationKey).trim().toLowerCase();
      const orientationOption = orientationFeature?.options?.find(opt_0 => String(opt_0.code).trim().toLowerCase() === orientationKeyStr);
      if (!orientationOption) {
        return;
      }
      return {
        [CYLINDO_OPTIONS.ORIENTATION]: orientationOption.code
      };
    };
    $[24] = cylindoProductConfig?.features;
    $[25] = orientationKey;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  cylindoProductConfig?.features;
  let t14;
  if ($[27] !== t13) {
    t14 = t13();
    $[27] = t13;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  const orientationOptionDetails = t14;
  const isOrientationFeatureAvailable = !!orientationOptionDetails;
  const areAllFeaturesAvailable = (!hasUpholsteryOption || hasUpholsteryOption && isUpholsteryFeatureAvailable) && (!hasOrientationOption || hasOrientationOption && isOrientationFeatureAvailable);
  let t15;
  if ($[29] !== upholsteryFeatureDetails) {
    t15 = upholsteryFeatureDetails || {};
    $[29] = upholsteryFeatureDetails;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== orientationOptionDetails) {
    t16 = orientationOptionDetails || {};
    $[31] = orientationOptionDetails;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] !== t15 || $[34] !== t16) {
    t17 = {
      ...t15,
      ...t16
    };
    $[33] = t15;
    $[34] = t16;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  const cylindoFeatures = t17;
  let t18;
  bb1: {
    if (isPDPDesktop && areAllFeaturesAvailable && Object.entries(cylindoFeatures).length) {
      let t19;
      if ($[36] !== cylindoFeatures) {
        t19 = Object.entries(cylindoFeatures).map(_temp4);
        $[36] = cylindoFeatures;
        $[37] = t19;
      } else {
        t19 = $[37];
      }
      const featuresString = t19.join("&feature=");
      let t20;
      if ($[38] !== featuresString || $[39] !== productId) {
        t20 = CYLINDO_FRAMES_TO_SHOW.map((frame, index) => ({
          id: frame,
          index,
          src: `${CYLINDO_CONTENT_API}/${CYLINDO_ACCOUNT_ID}/products/${productId}/frames/${frame}?feature=${featuresString}`,
          type: "cylindo"
        }));
        $[38] = featuresString;
        $[39] = productId;
        $[40] = t20;
      } else {
        t20 = $[40];
      }
      t18 = t20;
      break bb1;
    }
    let t19;
    if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
      t19 = [];
      $[41] = t19;
    } else {
      t19 = $[41];
    }
    t18 = t19;
  }
  const cylindoConfigurationPhotos = t18;
  const t19 = !!cylindoProductConfig && areAllFeaturesAvailable;
  const t20 = isCylindoProduct && !!cylindoProductConfig && areAllFeaturesAvailable && cylindoConfigurationPhotos?.length > 0;
  let t21;
  if ($[42] !== cylindoConfigurationPhotos || $[43] !== cylindoFeatures || $[44] !== dimensionShotsData || $[45] !== loading || $[46] !== t19 || $[47] !== t20 || $[48] !== upholsteryKey) {
    t21 = {
      cylindoConfigurationPhotos,
      cylindoFeatures,
      isCylindoAvailable: t19,
      loading,
      isCylindoConfigurationPhotos: t20,
      upholsteryKey,
      ...dimensionShotsData
    };
    $[42] = cylindoConfigurationPhotos;
    $[43] = cylindoFeatures;
    $[44] = dimensionShotsData;
    $[45] = loading;
    $[46] = t19;
    $[47] = t20;
    $[48] = upholsteryKey;
    $[49] = t21;
  } else {
    t21 = $[49];
  }
  const returnValue = t21;
  return returnValue;
};
export default usePDPCylindoViewer;
function _temp(e) {
  logError(e);
}
function _temp2(feat) {
  return feat.code === CYLINDO_OPTIONS.UPHOLSTERY;
}
function _temp3(feat_0) {
  return feat_0.code === CYLINDO_OPTIONS.ORIENTATION;
}
function _temp4(t0) {
  const [featureKey, featureCode] = t0;
  return `${featureKey}:${featureCode}`;
}